import { Paper } from '@mui/material';
import { RequestsWidget } from '@ankr.com/telemetry';

import { useTranslation } from 'modules/i18n/hooks/useTranslation';
import { ChainProtocolContext } from 'domains/chains/screens/ChainPage/constants/ChainProtocolContext';
import { isMultichain } from 'modules/chains/utils/isMultichain';
import { RequestsWidgetPlaceholder } from 'modules/stats/components/RequestsWidgetPlaceholder';
import { getRequestsChartTranslations } from 'modules/stats/utils/getRequestsChartTranslations';

import { RequestsMap } from '../../../RequestsMap';
import { PrivateUsageSummary } from './components/PrivateUsageSummary';
import { useDataUsageSectionStyles } from '../../UsageDataSectionStyles';
import { ItemHeader } from '../../../ItemHeader';
import { usageDataSectionTranslation } from '../../translation';
import { usePrivateUsageSection } from './usePrivateUsageSection';
import { PublicUsageDataSectionProps } from '../PublicUsageDataSection';
import { UsageSectionHeader } from '../UsageSectionHeader';

interface PrivateUsageDataSectionProps extends PublicUsageDataSectionProps {}

export const PrivateUsageDataSection = ({
  chain,
  timeframe,
  timeframeTabs,
}: PrivateUsageDataSectionProps) => {
  const {
    chainProtocolContext,
    chainSelectorProps,
    countries,
    isLoggingIn,
    loading,
    requestsChartData,
    totalRequests,
  } = usePrivateUsageSection(chain, timeframe);

  const { classes } = useDataUsageSectionStyles();

  const { keys, t } = useTranslation(usageDataSectionTranslation);

  return (
    <ChainProtocolContext.Provider value={chainProtocolContext}>
      <div className={classes.usageDataSection}>
        <Paper className={classes.statisticsPaper}>
          <UsageSectionHeader
            chain={chain}
            chainSelectorProps={chainSelectorProps}
            timeframe={timeframe}
            timeframeTabs={timeframeTabs}
            isMultichain={isMultichain(chain.id)}
          />
          <ItemHeader
            isLabelHidden
            timeframe={timeframe}
            title={t(keys.networkUsage)}
          />
          <div className={classes.row}>
            <PrivateUsageSummary
              loading={loading}
              timeframe={timeframe}
              totalRequests={totalRequests}
            />
          </div>
          <ItemHeader
            className={classes.statisticsItemTitle}
            isLabelHidden
            timeframe={timeframe}
            title={t(keys.totalRequests)}
          />
          <RequestsWidget
            NoDataPlaceholder={RequestsWidgetPlaceholder}
            className={classes.requestsWidget}
            data={requestsChartData}
            isLoading={loading || isLoggingIn}
            timeframe={timeframe}
            translation={getRequestsChartTranslations()}
            isTitleHidden
          />
          <ItemHeader
            className={classes.statisticsItemTitle}
            isLabelHidden
            timeframe={timeframe}
            title={t(keys.top10Countries)}
          />
          <RequestsMap
            isTitleHidden
            loading={loading}
            countries={countries}
            timeframe={timeframe}
          />
        </Paper>
      </div>
    </ChainProtocolContext.Provider>
  );
};
